import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { FaFilePdf, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCcDinersClub, FaCcJcb, FaCreditCard } from 'react-icons/fa'
import { Stack, Heading, Text, Badge, Button, Loading, EmptyBox, SimpleTable, Box, Alert, Card, ImageIcon, Icon, Select } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'

const PRESET_AMOUNTS = [50, 100, 250, 500]
const CURRENCY = 'GBP'

const CARD_ICONS = {
  visa: FaCcVisa,
  mastercard: FaCcMastercard,
  amex: FaCcAmex,
  discover: FaCcDiscover,
  diners: FaCcDinersClub,
  jcb: FaCcJcb
}

function CardBadge ({ brand, last4 }) {
  if (!last4) return null
  const CardIcon = CARD_ICONS[brand?.toLowerCase()] || FaCreditCard
  return (
    <Badge>
      <Stack direction='row' gap={2} alignItems='center'>
        <Icon as={CardIcon} fontSize='lg' color='fg.muted' />
        <Text fontSize='xs' color='fg.muted' fontFamily='monospace'>•••• {last4}</Text>
      </Stack>
    </Badge>
  )
}

function formatBalance (amount) {
  const n = parseFloat(amount ?? 0)
  if (Number.isNaN(n)) return '£0.00'
  const absPart = Math.abs(n).toFixed(2)
  if (n < 0) return `-£${absPart}`
  return `£${absPart}`
}

function formatTransactionAmount (amount) {
  // Show up to 4dp, trimming trailing zeros beyond the 2nd decimal place.
  // e.g. 0.039 → £0.039, 0.0065 → £0.0065, 1.5 → £1.50, 3.90 → £3.90
  let s = Math.abs(parseFloat(amount || 0)).toFixed(4)
  while (s.endsWith('0') && s.length > s.indexOf('.') + 3) {
    s = s.slice(0, -1)
  }
  return `£${s}`
}

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'general', label: 'General' },
  { value: 'video call', label: 'Video calls' },
  { value: 'message', label: 'Messages' },
  { value: 'gift card', label: 'Gift cards' }
]

const PER_PAGE = 30

export default function Wallet ({ trial }) {
  const { user } = useStore()
  const [balance, setBalance] = useState(null)
  const [prices, setPrices] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(true)
  const [txLoading, setTxLoading] = useState(false)
  const [topupLoading, setTopupLoading] = useState(false)
  const [customAmount, setCustomAmount] = useState('')

  function fetchTransactions (p, cat, isInitial) {
    if (isInitial) setLoading(true)
    else setTxLoading(true)
    api.trials.getWallet(trial._id, { page: p, perPage: PER_PAGE, category: cat || undefined }, (d) => {
      if (isInitial) setBalance(d.balance ?? 0)
      setTransactions(d.transactions || [])
      setTotalPages(d.totalPages ?? 1)
      setTotal(d.total ?? 0)
      setPrices(d.prices || {})
      setLoading(false)
      setTxLoading(false)
    }, () => { setLoading(false); setTxLoading(false) })
  }

  useEffect(() => {
    if (!trial?._id) return
    fetchTransactions(1, '', true)
  }, [trial?._id])

  function handleCategoryChange (e) {
    const val = e.target.value
    setCategory(val)
    setPage(1)
    fetchTransactions(1, val, false)
  }

  function handlePageChange (newPage) {
    setPage(newPage)
    fetchTransactions(newPage, category, false)
  }

  function adminAdjust () {
    const input = window.prompt('Enter amount to add (positive) or subtract (negative), e.g. 50 or -25:')
    if (input === null) return
    const amount = parseFloat(input)
    if (isNaN(amount) || amount === 0) {
      window.alert('Invalid amount.')
      return
    }
    const defaultDescription = amount > 0 ? 'Admin credit' : 'Admin debit'
    const description = window.prompt('Description:', defaultDescription)
    if (description === null) return
    const verb = amount > 0 ? `add £${amount.toFixed(2)}` : `subtract £${Math.abs(amount).toFixed(2)}`
    if (!window.confirm(`Are you sure you want to ${verb} from this trial's wallet?`)) return
    api.trials.adminAdjustWallet(trial._id, amount, description || defaultDescription, (d) => {
      setBalance(d.balance)
      setPage(1)
      fetchTransactions(1, category, false)
      utils.toast('success', 'Wallet adjusted')
    }, (err) => {
      utils.toast('error', 'Adjustment failed', err.message)
    })
  }

  function startTopup (amount) {
    const parsed = parseFloat(amount)
    if (!parsed || parsed < 20) {
      utils.toast('error', 'Minimum top-up is £20.00')
      return
    }
    setTopupLoading(true)
    api.trials.createTopupCheckout(trial._id, parsed, CURRENCY, ({ url }) => {
      window.location.href = url
    }, (err) => {
      setTopupLoading(false)
      utils.toast('error', 'Unable to start checkout', err.message)
    })
  }

  const [searchParams] = useSearchParams()
  const topupStatus = searchParams.get('topup')

  if (!utils.canManageTrialWallet(user, trial)) {
    return <Text>You don't have permission to view the wallet for this trial.</Text>
  }

  if (loading) return <Loading />

  return (
    <Stack gap={6}>
      <Box>
        <Heading size='md'>Wallet</Heading>
        <Text>Manage and track the funds associated with this trial. Funds are used to send messages and make video calls, and can also be used to purchase participant incentives and other items.</Text>
      </Box>
      {topupStatus === 'success' && (
        <Alert status='success' title='Top-up successful'>
          Your balance will be updated shortly once the payment is confirmed.
        </Alert>
      )}
      {topupStatus === 'cancelled' && (
        <Alert status='info' title='Top-up cancelled'>
          Your payment was not completed. No funds have been taken.
        </Alert>
      )}

      <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
        <Card minW='220px' gap={5} alignItems='start'>
          <Box><ImageIcon iconKey='wallet' size={0} /></Box>
          <Heading size='sm'>Wallet balance</Heading>
          <Box>
            <Heading size='xl'>{formatBalance(balance)}</Heading>
            <Text fontSize='xs' color='fg.muted'>Available to spend on this trial</Text>
          </Box>
          {utils.hasPermission(user, 'root', 'global') && (
            <Button size='xs' variant='outline' colorPalette='orange' onClick={adminAdjust}>
              Admin adjust
            </Button>
          )}
        </Card>

        {utils.canManageTrialWallet(user, trial) && (
          <>
            <Stack gap={4} p={5} rounded='md' bg='bg.surface' borderWidth='1px' flex={1}>
              <Heading size='sm'>Top up</Heading>
              <Stack gap={2}>
                <Stack direction='row' flexWrap='wrap' gap={2}>
                  {PRESET_AMOUNTS.map(amount => (
                    <Button
                      key={amount}
                      variant='outline'
                      size='sm'
                      onClick={() => startTopup(amount)}
                      isDisabled={topupLoading}
                    >
                      £{amount}
                    </Button>
                  ))}
                </Stack>
                <Stack direction='row' gap={2} alignItems='center'>
                  <input
                    type='number'
                    min='20'
                    step='1'
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    placeholder='Custom amount (£)'
                    style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 10px', width: '160px', fontSize: '14px' }}
                  />
                  <Button
                    size='sm'
                    colorPalette='primary'
                    onClick={() => startTopup(customAmount)}
                    loading={topupLoading}
                    isDisabled={!customAmount}
                  >
                    Top up
                  </Button>
                </Stack>
                <Text fontSize='xs' color='fg.muted'>Minimum top-up is £20. Payments are processed securely by Stripe. Invoices are available for download after payment is confirmed.</Text>
              </Stack>
              <Alert fontSize='sm'>If you need to move leftover funds to another trial, please contact your account manager.</Alert>
            </Stack>

            <Stack gap={4} p={5} rounded='md' bg='bg.surface' borderWidth='1px' maxW='400px'>
              <Heading size='sm'>Current pricing</Heading>
              <Text fontSize='xs' color='fg.muted'>Pricing may fluctuate slightly from time to time due to changes in our provider costs.</Text>
              <SimpleTable
                size='sm'
                headers={['Item', 'Price (GBP)']}
                rows={[
                  ['Video call (participant minute)', prices.wherebyVideoCallMinute],
                  ['Video call (transcription minute)', prices.wherebyTranscriptionMinute],
                  ['SMS (segment)', prices.birdSmsUnit],
                  ['WhatsApp (notifications, support, invitations)', prices.birdWhatsappUtilityUnit],
                  ['WhatsApp (reminders)', prices.birdWhatsappMarketingUnit],
                  ['WhatsApp (codes)', prices.birdWhatsappAuthenticationUnit],
                  ['WhatsApp (reply in chat)', prices.birdWhatsappServiceUnit],
                  ['Identity check', <Badge key='kyc' size='xs'>{prices.kycaidVerificationMarkup ? `Provider EUR price +${Math.round((prices.kycaidVerificationMarkup - 1) * 100)}%` : 'As billed'}</Badge>],
                  ['Voucher / gift card', <Badge key='voucher' size='xs'>As billed</Badge>]
                ]}
              />
            </Stack>
          </>
        )}
      </Stack>

      <Stack gap={3}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
          <Heading size='sm'>Transaction history {total > 0 && <Text as='span' fontSize='sm' fontWeight='normal' color='fg.muted'>({total})</Text>}</Heading>
          <Select
            value={category}
            onChange={handleCategoryChange}
            size='sm'
            w='180px'
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
        </Stack>
        {txLoading
          ? <Loading />
          : transactions.length === 0
            ? (
              <EmptyBox title='No transactions yet'>
                <Text>Top-ups and purchases will appear here.</Text>
              </EmptyBox>
              )
            : (
              <>
                <SimpleTable
                  asCard
                  size='sm'
                  headers={['Date', 'Description', 'Amount', 'Balance after', '']}
                  rows={transactions.map(tx => [
                    <Text key='date' fontSize='sm'>{utils.formatDateTime(tx.createdAt)}</Text>,
                    <Stack key='desc' gap={3} direction='row' alignItems='center'>
                      <Text fontSize='sm'>{tx.description}</Text>
                      <CardBadge key='method' brand={tx.cardBrand} last4={tx.cardLast4} />
                    </Stack>,
                    <Badge
                      key='amount'
                      colorPalette={tx.type === 'credit' ? 'green' : 'red'}
                      variant='subtle'
                      size='sm'
                    >
                      {tx.type === 'credit' ? '+' : '-'}{formatTransactionAmount(tx.amount)}
                    </Badge>,
                    <Text key='balance' fontSize='sm' fontFamily='monospace'>{formatBalance(tx.balanceAfter)}</Text>,
                    <Stack key='actions' direction='row' gap={2} justifyContent='end'>
                      {tx.invoiceUrl && <Button size='xs' variant='ghost' as='a' href={tx.invoiceUrl} target='_blank' rel='noopener noreferrer'><Icon as={FaFilePdf} /> Download invoice</Button>}
                    </Stack>
                  ])}
                />
                {totalPages > 1 && (
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Text fontSize='sm' color='fg.muted'>Page {page} of {totalPages}</Text>
                    <Stack direction='row' gap={2}>
                      <Button size='xs' variant='outline' onClick={() => handlePageChange(page - 1)} isDisabled={page <= 1}>Previous</Button>
                      <Button size='xs' variant='outline' onClick={() => handlePageChange(page + 1)} isDisabled={page >= totalPages}>Next</Button>
                    </Stack>
                  </Stack>
                )}
              </>
              )}
      </Stack>
    </Stack>
  )
}
